import dayjs, { Dayjs } from "dayjs"
import { TKyuzituListGetRes } from "../../service/master/kyuzitu/TKyuzitu"
import * as wjcCore from "@grapecity/wijmo"
import * as wjcInput from "@grapecity/wijmo.input"
import * as wjcGrid from "@grapecity/wijmo.grid"
import { e休日区分 } from "../../constant/enum/master"
import stringUtil from "./stringUtil"

/**
 * フォーマット
 * @param date
 * @param format 未設定の場合YYYY/MM/DD
 * @returns
 */
const format = (
  date: string | Date | undefined | null,
  format: string = "YYYY/MM/DD"
): string => {
  if (date == null) return ""
  return dayjs(date).isValid() ? dayjs(date).format(format) : date.toString()
}

/**
 * 入力した日の次の翌営業日を取得する
 * @param date
 */
const toNextEigyobi = (date: string | Date): Date => {
  // 取得したstringもしくは DateをDayJsに変換
  let yokueigyobi: dayjs.Dayjs = dayjs(date).add(1, "day")

  return yokueigyobi.toDate()
}

/**
 * 入力した日の次の翌営業日を所定フォーマットの文字列で取得する
 * @param date
 * @param format 未設定の場合YYYY/MM/DD
 */
const toNextEigyobiStr = (
  date: string | Date,
  format: string = "YYYY/MM/DD"
): string => {
  return dayjs(toNextEigyobi(date)).format(format)
}

const addDay = (day: Date | string, count: number): Date => {
  const dayjsDate = dayjs(day)

  return dayjsDate.add(count, "day").toDate()
}

const add = (
  origin: Date | string,
  adds: { year?: number; month?: number; day?: number }
): Date => {
  let dayjsDate = dayjs(origin)

  if (adds.year) {
    dayjsDate = dayjsDate.add(adds.year, "year")
  }
  if (adds.month) {
    dayjsDate = dayjsDate.add(adds.month, "month")
  }
  if (adds.day) {
    dayjsDate = dayjsDate.add(adds.day, "day")
  }
  return dayjsDate.toDate()
}

const getNowString = () => {
  const now = dayjs()
  return now.format("MM/DD HH:mm")
}

/**
 * 前月を取得する
 * @param date
 * @returns
 */
const toZenNenGetsu = (date: string | Date): Date => {
  return dayjs(date).subtract(1, "month").toDate()
}

/**
 * 前月をを所定フォーマットの文字列で取得する
 * @param date
 * @param format 未設定の場合YYYY/MM/DD
 * @returns
 */
const toZenNenGetsuStr = (
  date: string | Date,
  format: string = "YYYY/MM/DD"
): string => {
  return dayjs(toZenNenGetsu(date)).format(format)
}

/**
 * 日付をISO形式フォーマットで取得
 * @param inputDataValue
 */
const toInputDataValueToDateString = (
  inputDataValue: string | Date | undefined
): string | undefined => {
  if (
    inputDataValue == null ||
    inputDataValue === "" ||
    !dayjs(inputDataValue).isValid()
  )
    return undefined
  return dayjs(inputDataValue).toISOString()
}

/**
 * 日付と休日Listより休日祝日を判定する
 * 0: 平日
 * 1: 日曜日
 * 2: 休日
 * @param 当日
 * @param 休日List
 * @returns
 */
const HolidayCheck = (
  当日: string | Date | Dayjs | undefined,
  休日List: TKyuzituListGetRes[]
) => {
  if (当日 == null) return 0
  if (!dayjs(当日).isValid()) return 0
  const 当日dayjs = dayjs(当日)
  // 日曜日は休日
  if (当日dayjs.day() === 0) return 1
  // 所定休日以外の休日
  const inner休日List = 休日List
    .filter((i) => i.休日区分コード !== e休日区分.所定休日)
    .map((item) => item.休日日付)
  return inner休日List.some((str休日) =>
    当日dayjs.isSame(dayjs(str休日), "date")
  )
    ? 2
    : 0
}

const inputDateOffDayFormatHandler: (
  休日List?: TKyuzituListGetRes[]
) => wjcCore.IEventHandler<wjcInput.Calendar, wjcInput.FormatItemEventArgs> =
  (休日List) => (control, event) => {
    let 当日 = event.data ? dayjs(event.data) : undefined
    if (当日 != null) {
      wjcCore.toggleClass(event.item, "date-sunday", 当日.day() === 0)

      if (休日List) {
        // 所定休日以外の休日
        const inner休日List = 休日List
          .filter((i) => i.休日区分コード !== e休日区分.所定休日)
          .map((item) => item.休日日付)

        wjcCore.toggleClass(
          event.item,
          "date-holiday",
          inner休日List.some((str休日) => 当日!.isSame(dayjs(str休日), "date"))
        )
        //文言も出す
        const 休日 = 休日List.find((item) =>
          当日!.isSame(item.休日日付, "date")
        )
        event.item.title = 休日 ? 休日.祝日名 ?? "" : ""
      }
    }
  }

/**
 * 在籍年月数を割り出し、xx年xxヶ月文字列で返却
 * @param 営業日
 * @param 入社年月日
 * @param 退職年月日
 * @returns 在籍年数・在籍月数をxx年xxヶ月の文字列で返却。不正パラメータの場合は空文字返却
 */
const to在籍年月数Str = (
  営業日: string,
  入社年月日?: string | null,
  退職年月日?: string | null
): string => {
  if (stringUtil.isNullOrWhiteSpace(入社年月日)) return ""
  const 在籍年月数 = dateUtil.to在籍年月数(
    営業日,
    入社年月日!,
    退職年月日 ?? ""
  )
  return `${在籍年月数.year}年${在籍年月数.month}ヶ月`
}

/**
 * 在籍年月数を割り出し
 * @param 営業日
 * @param 入社年月日
 * @param 退職年月日
 * @returns 在籍年数・在籍月数
 */
const to在籍年月数 = (
  営業日: string,
  入社年月日: string,
  退職年月日?: string
): { year: number; month: number } => {
  const dayjs営業日 = dayjs(営業日)

  // 退職年月日が存在し、退職年月日が営業日より前の場合は退職年月日を基準日とする
  let baseDay
  if (!stringUtil.isNullOrWhiteSpace(退職年月日)) {
    const dayjs退職年月日 = dayjs(退職年月日)
    baseDay = dayjs退職年月日 < dayjs営業日 ? dayjs退職年月日 : dayjs営業日
  } else {
    baseDay = dayjs営業日
  }

  // 入社年月日と基準日から差分を割り出し
  const diffMonth = baseDay.diff(dayjs(入社年月日), "month")
  const diffYear = Math.trunc(diffMonth / 12)
  const modMonth = diffMonth % 12
  return { year: diffYear, month: modMonth }
}

/**
 * 年齢を割り出し
 * @param 営業日
 * @param 生年月日
 * @returns 在籍年数・在籍月数
 */
const calc年齢 = (
  営業日: string | null | undefined,
  生年月日: string | null | undefined
): number | undefined => {
  if (生年月日 == null || !dayjs(生年月日).isValid()) return undefined

  // 営業日が使えない場合、当日の日付を使う
  const dayjs営業日 = dayjs(営業日).isValid() ? dayjs(営業日) : dayjs()
  // 生年月日と営業日から誕生日を計算する
  const 年齢 = dayjs営業日.diff(dayjs(生年月日), "year")
  return 年齢
}

/**
 * 和暦を取得する
 * @param date
 * @returns
 */
const get和暦 = (date: string | null | undefined) =>
  dayjs(date).isValid()
    ? new Intl.DateTimeFormat("ja-JP", {
        year: "numeric",
        calendar: "japanese",
      }).format(dayjs(date).toDate())
    : ""

/**
 * 和暦(西暦)/MM/DDに変換する
 * @returns string
 */
const show和暦含Format = (date: string | null | undefined) =>
  date != null
    ? date !== ""
      ? dayjs(date).isValid()
        ? `${get和暦(date)} (${dayjs(date).format("YYYY")})/${dayjs(
            date
          ).format("MM/DD")}`
        : ""
      : ""
    : ""

const 和暦表示開始年 = 1945
export type T年号西暦一覧 = {
  西暦: number
  和暦: string
  displayMemberPath: string
}
export const create年号西暦一覧 = (lastYear: number = 2100) => {
  // 和暦表示開始年よりlastYearまでの年号と西暦の組み合わせを返却する
  // デフォルトは1945年から西暦2100年までのリストを作成
  let 経過年数 = 0
  let max = lastYear - 1926
  const returnArray: T年号西暦一覧[] = []
  for (経過年数; 経過年数 <= max; 経過年数++) {
    const 西暦 = 和暦表示開始年 + 経過年数
    const 和暦 = get和暦(dayjs().year(西暦).month(1).date(1).toISOString())
    returnArray.push({
      西暦,
      和暦,
      displayMemberPath: `${和暦} (${西暦})`,
    })
  }
  return returnArray
}

// 月表示一覧
// そのままdataMapにも使えるようにkeyvalueで保持
export const 月一覧 = [
  { key: "1月", value: 0 },
  { key: "2月", value: 1 },
  { key: "3月", value: 2 },
  { key: "4月", value: 3 },
  { key: "5月", value: 4 },
  { key: "6月", value: 5 },
  { key: "7月", value: 6 },
  { key: "8月", value: 7 },
  { key: "9月", value: 8 },
  { key: "10月", value: 9 },
  { key: "11月", value: 10 },
  { key: "12月", value: 11 },
]

export const 当月一覧生成 = (date: string | Date) => {
  if (!dayjs(date).isValid) {
    return []
  }
  return 当月一覧生成ByNumber(dayjs(date).year(), dayjs(date).month())
}

export const 当月一覧生成ByNumber = (year: number, month: number) => {
  const endDate = dayjs().year(year).month(month).endOf("M").date()
  let date = 1
  const returnArray: { key: string; value: number }[] = []
  for (; date <= endDate; date++) {
    returnArray.push({
      key: `${date}日`,
      value: date,
    })
  }

  return returnArray
}

/**
 * wijmo経由での記述をiso 8601形式に変換する
 * outerFormatでの変換も可
 * @param dateString 変換対象
 * @param parseFormats date変換時に使用するformat 和暦の年号に対応するため、ここだけwijmo準拠
 * @param outerFormat 出力format 基本となるdayjs準拠
 * @returns ISO 8601 string もしくは指定のouterFormat
 */
const toStringByFormat = (
  dateString: string,
  parseFormats: string[],
  outerFormat?: string
): string => {
  // wijmoのparseDateを使い、年号付も含めたフォーマットをDateに一度変換する
  let dateList = parseFormats
    .map((parseFormat) => wjcCore.Globalize.parseDate(dateString, parseFormat))
    .filter((d) => wjcCore.isDate(d))

  // 組み合わせ合致がしている正常なもの以外失敗するが、ISOStringの場合もあるのでISOStringの場合の処理を行う
  if (dateList.length <= 0) {
    if (!dayjs(dateString).isValid()) return ""
    return outerFormat
      ? dayjs(dateString).format(outerFormat)
      : dayjs(dateString).toISOString()
  }
  return outerFormat
    ? dayjs(dateList[0]).format(outerFormat)
    : dayjs(dateList[0]).toISOString()
}

/**
 * wijmo経由での和暦含む記述をiso 8601形式に変換する
 * "yyyyMMdd"
 * "ggyyMMdd"
 * "yyyy/MM/dd"
 * "ggyy/MM/dd"
 * @param dateString  変換対象
 * @returns ISO 8601 string
 */
const toStringBy和暦含Format = (dateString: string | null | undefined) => {
  if (dateString == null || wjcCore.isNullOrWhiteSpace(dateString)) return ""

  return toStringByFormat(dateString, [
    "yyyyMMdd",
    "ggyyMMdd",
    "yyyy/MM/dd",
    "ggyy/MM/dd",
  ])
}

// 年号選択用dataMap
export const 和暦西暦DataMap = new wjcGrid.DataMap(
  create年号西暦一覧(),
  "西暦",
  "displayMemberPath"
)

/**
 * 年度を取得する
 * @param date
 */
const to年度 = (date: string | Date): number => {
  const month = dayjs(date).month()
  if (month < 4) {
    return dayjs(date).year() - 1
  } else {
    return dayjs(date).year()
  }
}

export type TMonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

const dateUtil = {
  format,
  toNextEigyobi,
  toNextEigyobiStr,
  addDay,
  add,
  getNowString,
  toZenNenGetsu,
  toZenNenGetsuStr,
  toInputDataValueToDateString,
  HolidayCheck,
  inputDateOffDayFormatHandler,
  toIsoStringByFormat: toStringByFormat,
  toISoDateStringBy和暦含Format: toStringBy和暦含Format,
  to在籍年月数,
  to在籍年月数Str,
  calc年齢,
  get和暦,
  show和暦含Format,
  create年号西暦一覧,
  月一覧,
  当月一覧生成,
  当月一覧生成ByNumber,
  to年度,
}

export default dateUtil
